/**
 * Timezone-safe date helpers for the Payroll / Staff Schedule / Client Care
 * Cost pages.
 *
 * NEVER use `Date.prototype.toISOString().slice(0, 10)` to format a local-day
 * `YYYY-MM-DD` here — in any timezone east of UTC (e.g. UK BST, Africa/Lagos,
 * Asia/Kolkata) local midnight is `previous-day 23:xx` in UTC, which shifts
 * the returned date back by one day. That single-day shift is what caused
 * the "only 1 of 5 shifts reaching payroll" bug: `mondayOf()` produced a
 * Sunday instead of a Monday, and the payroll period silently pointed to
 * the previous Mon-Sun week.
 *
 * A Jest regression guard (see `dateWeek.test.js`) pins:
 *   - formatLocalYMD renders the local Y/M/D — not the UTC one
 *   - mondayOf(any date) is always a Monday (weekday === 1)
 *   - addDays roundtrip preserves the local date
 *   - the source of this file contains no `toISOString().slice(0, 10)` reference
 */

/** Format a Date as `YYYY-MM-DD` from its LOCAL Y/M/D components. */
export function formatLocalYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** ISO `YYYY-MM-DD` for today, using LOCAL calendar (never shifts across TZ). */
export function todayISO() {
  return formatLocalYMD(new Date());
}

/**
 * Monday (ISO weekday 1) of the week containing `dateStr` (defaults to today).
 * Weeks are Mon–Sun. Returns `YYYY-MM-DD` in LOCAL calendar.
 */
export function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr + "T00:00:00") : new Date();
  // JS getDay(): Sun=0..Sat=6 → convert to Mon=0..Sun=6
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return formatLocalYMD(d);
}

/** Add `n` calendar days to a `YYYY-MM-DD` string. TZ-safe. */
export function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return formatLocalYMD(d);
}

/** Add `n` weeks (7 * n days). TZ-safe. */
export function addWeeks(iso, n) {
  return addDays(iso, n * 7);
}
